/**
 * Publishes the height of a screen's sticky foot as `--sticky-foot-height` on `<html>`, so
 * that fixed chrome elsewhere can sit *above* it instead of guessing a clearance.
 *
 * The one caller is the story screen, and the one reader is the global feedback button. A
 * constant would have done if the foot were a constant height, and it is not: the action bar
 * carries the DM's suggested choices as wrapped chips, so the block is one row tall with none
 * and three rows taller with four long ones in German. Anything hard-coded is wrong on some
 * turn, in some language, and the failure mode is a control sitting on top of the input the
 * player is trying to use.
 *
 * `getBoundingClientRect()` rather than the entry's `contentRect`, because the foot's border
 * and the action bar's padding are part of what has to be cleared.
 *
 * **It hands back a ref *callback*, not a ref object, and that is the whole correctness of the
 * hook.** The version this replaces kept a `useRef` and set the observer up in a `useEffect`
 * with no dependencies — which ran on the story screen's first commit, and the story screen's
 * first commit renders *"Loading the story…"* while `GET /chapters` is in flight. There is no
 * foot in that tree, so `ref.current` was null, the effect took its early return, and with an
 * empty dependency list it never ran again: the property was never published on any screen, the
 * `0px` fallback in `.feedback-anchor` always applied, and on a phone the feedback button sat
 * on top of the action bar. A ref callback fires when the node actually attaches, which is one
 * render later, and `setNode` is a state setter so its identity is stable and React does not
 * detach and re-attach it on every render. `tests/e2e/phone.spec.ts` is what caught it.
 */
import { useEffect, useState } from 'react';

const PROPERTY = '--sticky-foot-height';

export function useStickyFootHeight(): (node: HTMLDivElement | null) => void {
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    // jsdom has no layout and no `ResizeObserver`; the property simply stays unset and the
    // reader's `0px` fallback applies, which is the right answer for a screen with no size.
    if (!node || typeof ResizeObserver === 'undefined') return;

    const root = document.documentElement;
    const observer = new ResizeObserver(() => {
      root.style.setProperty(PROPERTY, `${String(node.getBoundingClientRect().height)}px`);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      // Removed rather than zeroed: the next screen may have no foot at all, and a stale
      // value would push the button up over nothing.
      root.style.removeProperty(PROPERTY);
    };
  }, [node]);

  return setNode;
}
